import React from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import './Map.css';
import bbox from '@turf/bbox';
import { inside } from '@turf/turf';
import { featureCollection, feature, point, polygon } from '@turf/helpers';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import isEqual from 'lodash.isequal';
import EditModeMsg from './EditModeMsg/EditModeMsg';

const transformRequest = (url, resourceType) => {
  const isMapboxRequest =
    url.slice(8, 22) === 'api.mapbox.com' ||
    url.slice(10, 26) === 'tiles.mapbox.com';
  return {
    url: isMapboxRequest
      ? url.replace('?', '?pluginName=optimize-tuner&')
      : url,
  };
};

const defaultData = {
  type: 'FeatureCollection',
  features: [],
};

const RoutesPopup = ({ routesInPoly, onSelect }) => (
  <div className="routes-popup">
    {routesInPoly.map(route => (
      <div key={route} onClick={() => onSelect(route)}>
        Route <span>{route}</span>
      </div>
    ))}
  </div>
);
class MapWidget extends React.Component {
  hoverPostalId;
  selectPostalId;
  constructor() {
    super();
    this.state = {
      routeLayerEnabled: false,
    };
  }
  stopsToGeoJSON = stops => {
    if (stops['type'] === 'FeatureCollection') {
      return stops;
    } else if (stops.length) {
      const features = stops.map(stop => {
        return {
          id: stop.id,
          properties: stop,
          geometry: stop.geometry,
        };
      });
      return featureCollection(features);
    } else {
      return defaultData;
    }
  };

  routesToGeoJSON = routes => {
    if (routes.length) {
      const features = routes.map(route => {
        return {
          id: route.id,
          properties: route,
          geometry: route.geometry,
        };
      });
      return featureCollection(features);
    }
  };

  updateSymbols = fc => {
    this.theMap.getSource('stops').setData(fc);
    this.zoomToData(fc);
  };

  zoomToData = fc => {
    if (!this.props.editMode) {
      const value = bbox(fc);
      this.theMap.fitBounds(value, { padding: 50, maxZoom: 13 });
    }
  };

  setRouteFeature = (routeId, feature = 'hover', force = false) => {
    const source = `${feature}Route`;
    const stopsSource = `${feature}RouteStops`;
    const sourceLayer = this.theMap.getSource(source);
    const stopsSourceLayer = this.theMap.getSource(stopsSource);
    if (!sourceLayer || !stopsSourceLayer) return;
    if (!routeId) {
      sourceLayer.setData(defaultData);
      stopsSourceLayer.setData(defaultData);
    } else {
      const selectedRoute = this.props.routes.find(item => item.id === routeId);
      sourceLayer.setData(this.routesToGeoJSON([selectedRoute]));
      stopsSourceLayer.setData(this.stopsToGeoJSON(selectedRoute.stops));
    }
  };

  zoomToRoute = routeId => {
    if (!routeId) {
      this.zoomToData(this.stopsToGeoJSON(this.props.stops));
    } else if (this.theMap.getZoom() <= 13) {
      const selectedRoute = this.props.routes.find(item => item.id == routeId);
      const stopsFc = this.stopsToGeoJSON(selectedRoute.stops);
      this.zoomToData(stopsFc);
    }
  };

  selectRoute = (prevRouteId, routeId) => {
    this.setRouteFeature(routeId, 'select');
    routeId && this.zoomToRoute(routeId);
  };

  selectStop = (prevStopId, stopId) => {
    if (prevStopId) {
      this.theMap.setFeatureState(
        { source: 'stops', id: prevStopId },
        { active: false }
      );
    }
    if (stopId) {
      this.theMap.setFeatureState(
        { source: 'stops', id: stopId },
        { active: true }
      );
      this.showPopup(stopId);
    } else if (!stopId) {
      this.hidePopup();
    }
  };

  selectRouteFromPopup = routeId => {
    this.hidePopup();
    this.props.onSelectRoute(routeId);
  };

  setPostalFeature = (postalFeature, feature = 'hover', coords) => {
    const polyLayerName = 'boundaries_postal_2';
    const source = 'postal-2';
    if (feature === 'select') {
      this.resetSelectedPostal();
    }
    // show the popup with the list of routes within the postal
    if (coords) {
      // some of postal has several polygons
      const polygonFt = polygon(
        postalFeature.geometry.coordinates.length > 1
          ? postalFeature.geometry.coordinates.flat()
          : postalFeature.geometry.coordinates
      );
      const res = Array.from(
        this.props.stops
          .filter(item => inside(point(item.geometry.coordinates), polygonFt))
          .reduce((acc, item) => acc.add(item.route_id), new Set())
      );
      if (!res.length) return;
      const div = document.createElement('div');
      ReactDOM.render(
        React.createElement(RoutesPopup, {
          routesInPoly: res,
          onSelect: this.selectRouteFromPopup,
        }),
        div
      );
      this.popup
        .setLngLat(coords)
        .setDOMContent(div)
        .addTo(this.theMap);
    }

    if (this[`${feature}PostalId`] === postalFeature.id) {
      return;
    }
    this[`${feature}PostalId`] &&
      this.theMap.setFeatureState(
        {
          source: source,
          sourceLayer: polyLayerName,
          id: this[`${feature}PostalId`],
        },
        { [feature]: 0 }
      );
    this[`${feature}PostalId`] = postalFeature.id;
    this.theMap.setFeatureState(
      {
        source: source,
        id: this[`${feature}PostalId`],
        sourceLayer: polyLayerName,
      },
      { [feature]: 1 }
    );
  };
  resetSelectedPostal = () => {
    const polyLayerName = 'boundaries_postal_2';
    const source = 'postal-2';
    this.selectPostalId &&
      this.theMap.removeFeatureState({
        source: source,
        sourceLayer: polyLayerName,
        id: this.selectPostalId,
      });
    this.selectPostalId = null;
    this.popup.remove();
  };

  componentDidMount() {
    mapboxgl.accessToken =
      process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ||
      'pk.eyJ1IjoieXVsZXRpZGUiLCJhIjoiY2s3cHMyeWxtMDQwaDNmbXZkeTlvMnRmayJ9.mkYPHtQ5iLxGEufBPKOsBg';
    this.theMap = new mapboxgl.Map({
      container: this.mapContainer,
      style: 'mapbox://styles/mapbox/light-v9?optimize=true',
      hash: true,
      transformRequest: transformRequest,
    });

    this.theMap.on('load', () => {
      // BOUNDARIES
      this.theMap.addSource('postal-3', {
        type: 'vector',
        url: 'mapbox://mapbox.boundaries-pos3-v3',
      });
      this.theMap.addSource('postal-2', {
        type: 'vector',
        url: 'mapbox://mapbox.boundaries-pos2-v3',
      });
      this.theMap.addLayer(
        {
          id: 'admin3',
          type: 'line',
          source: 'postal-3',
          'source-layer': 'boundaries_postal_3',
          paint: {
            'line-color': '#bbb',
            'line-width': 0.5,
          },
        },
        'waterway-label'
      );
      this.theMap.addLayer(
        {
          id: 'admin2',
          type: 'fill',
          source: 'postal-2',
          'source-layer': 'boundaries_postal_2',
          paint: {
            'fill-color': 'rgba(0,0,0,0)',
          },
        },
        'waterway-label'
      );

      this.theMap.addLayer(
        {
          id: 'selectedPostal',
          type: 'line',
          source: 'postal-2',
          'source-layer': 'boundaries_postal_2',
          paint: {
            'line-color': [
              'case',
              ['==', ['feature-state', 'select'], 1],
              '#42505A',
              ['==', ['feature-state', 'hover'], 1],
              '#42505A',
              '#7E8992',
            ],
            'line-width': [
              'case',
              ['==', ['feature-state', 'select'], 1],
              1.5,
              ['==', ['feature-state', 'hover'], 1],
              1.5,
              1,
            ],
          },
        },
        'waterway-label'
      );

      // ROUTES
      this.theMap.addSource('routes', {
        type: 'geojson',
        data: defaultData,
        // set to a field once we have sample data
        // promoteId: 'stop_ id'
      });
      this.theMap.addLayer({
        id: 'routes',
        type: 'line',
        source: 'routes',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
          visibility: this.state.routeLayerEnabled ? 'visible' : 'none',
        },
        paint: {
          'line-color': '#C86D6D',
          'line-width': 1,
        },
      });

      // STOPS
      this.theMap.addSource('stops', {
        type: 'geojson',
        data: defaultData,
      });
      this.theMap.addLayer({
        id: 'stops',
        type: 'circle',
        source: 'stops',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 1.5, 15, 6],
          'circle-stroke-width': ['case', ['feature-state', 'active'], 8, 0],
          'circle-stroke-color': [
            'case',
            ['boolean', ['feature-state', 'active'], false],
            'rgba(255,255,255,0.8)', // blue
            '#D75050', // red
          ],
          'circle-color': [
            'case',
            ['boolean', ['feature-state', 'active'], false],
            '#0066FF', // blue
            '#42505A',
          ],
        },
      });

      this.updateDataLayers();

      // SELECTED ROUTE
      this.theMap.addSource('selectRoute', {
        type: 'geojson',
        data: defaultData,
      });
      this.theMap.addLayer({
        id: 'selectRoute',
        type: 'line',
        source: 'selectRoute',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#D75050',
          'line-width': 2,
        },
      });

      // HIGHLIGHTED ROUTE
      this.theMap.addSource('hoverRoute', {
        type: 'geojson',
        data: defaultData,
      });
      this.theMap.addLayer({
        id: 'hoverRoute',
        type: 'line',
        source: 'hoverRoute',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#0066FF',
          'line-width': 1,
        },
      });

      // SELECTED ROUTE STOPS
      this.theMap.addSource('selectRouteStops', {
        type: 'geojson',
        data: defaultData,
      });
      this.theMap.addLayer({
        id: 'selectRouteStops',
        type: 'circle',
        source: 'selectRouteStops',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 2, 2, 5],
          'circle-stroke-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            1,
            2,
            2,
            2,
          ],
          'circle-color': [
            'case',
            ['boolean', ['feature-state', 'active'], false],
            // '#787F87', // dark gray
            'yellow', // grey
            '#FFFFFF', // red
          ],
          'circle-stroke-color': '#D75050',
        },
      });

      // SELECTED ROUTE STOPS
      this.theMap.addSource('hoverRouteStops', {
        type: 'geojson',
        data: defaultData,
      });
      this.theMap.addLayer({
        id: 'hoverRouteStops',
        type: 'circle',
        source: 'hoverRouteStops',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 6, 1.5, 15, 6],
          'circle-color': '#0066FF',
        },
      });

      // STOPS TO OPTIMIZE
      this.theMap.addSource('stopsToOptimize', {
        type: 'geojson',
        data: defaultData,
      });
      this.theMap.addLayer({
        id: 'stopsToOptimize',
        type: 'circle',
        source: 'stopsToOptimize',
        paint: {
          'circle-radius': ['interpolate', ['linear'], ['zoom'], 1, 2, 2, 5],
          'circle-stroke-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            1,
            2,
            3,
            4,
          ],
          'circle-color': [
            'case',
            ['boolean', ['feature-state', 'active'], false],
            // '#787F87', // dark gray
            'black', // grey
            '#42505A', // red
          ],
          'circle-stroke-color': '#DBFF00',
        },
      });

      // CONNECTED STOPS
      this.theMap.addSource('connectedStops', {
        type: 'geojson',
        data: defaultData,
      });
      this.theMap.addLayer({
        id: 'connectedStops',
        type: 'line',
        source: 'connectedStops',
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
        paint: {
          'line-color': '#0066FF',
          'line-width': 1,
        },
      });

      this.zoomToRoute();
      this.theMap.on('mousemove', 'admin2', e => {
        this.setPostalFeature(e.features[0], 'hover');
      });
      this.theMap.on('click', e => {
        const features = this.theMap.queryRenderedFeatures(e.point);
        const stop = features.find(item => item.layer.id === 'stops');
        const postal = features.find(item => item.layer.id === 'admin2');

        if (stop || (!stop && this.props.selectedStop)) {
          if (this.props.editMode) {
            this.props.onSelectedStopsUpdate(stop.id);
          } else {
            this.props.onStopClick(stop ? stop.id : null);
            return;
          }
        } else if (postal) {
          this.props.selectedRoute && this.props.onSelectRoute(null);
          this.setPostalFeature(postal, 'select', e.lngLat);
        }
      });

      this.theMap.on('mouseenter', 'stops', e => {
        const routeId = e.features[0].properties['route_id'];
        this.theMap.getCanvas().style.cursor = 'pointer';
        routeId !== this.props.selectedRoute &&
          this.props.onRouteHover(routeId);
        if (this.props.editMode) {
          this.showPopup(e.features[0].properties.id);
        }
      });

      this.theMap.on('mouseleave', 'stops', e => {
        this.theMap.getCanvas().style.cursor = '';
        this.props.onRouteHover(null);
        if (this.props.editMode) {
          this.hidePopup();
        }
      });
    });

    this.popup = new mapboxgl.Popup({ closeOnClick: false });
  }

  updateDataLayers() {
    const stopsFc = this.stopsToGeoJSON(this.props.stops);
    const routesFC = this.routesToGeoJSON(this.props.routes);
    this.theMap.getSource('stops').setData(stopsFc);
    this.theMap.getSource('routes').setData(routesFC);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (prevProps.selectedStop !== this.props.selectedStop) {
      this.selectStop(prevProps.selectedStop, this.props.selectedStop);
    }
    if (prevProps.selectedRoute !== this.props.selectedRoute) {
      this.selectRoute(prevProps.selectedRoute, this.props.selectedRoute);
    }
    if (
      prevProps.highlightedRoute !== this.props.highlightedRoute ||
      (this.props.highlightedRoute &&
        this.props.highlightedRoute !== this.props.selectedRoute)
    ) {
      this.setRouteFeature(this.props.highlightedRoute, 'hover');
    }
    if (!isEqual(prevProps.stopsToOptimize, this.props.stopsToOptimize)) {
      const stops = this.props.stops.filter(
        item => this.props.stopsToOptimize[item.id]
      );
      const source = this.theMap.getSource('stopsToOptimize');
      source && source.setData(this.stopsToGeoJSON(stops));
    }
    if (!isEqual(prevProps.stops, this.props.stops)) {
      this.updateDataLayers();
      this.setRouteFeature(this.props.selectedRoute, 'select', true);
    }
  }

  toggleRoutesLayer = e => {
    e.preventDefault();
    e.stopPropagation();

    if (this.state.routeLayerEnabled) {
      this.theMap.setLayoutProperty('routes', 'visibility', 'none');
    } else {
      this.theMap.setLayoutProperty('routes', 'visibility', 'visible');
    }
    this.setState({
      routeLayerEnabled: !this.state.routeLayerEnabled,
    });
  };

  showPopup = id => {
    const stop = this.props.stops.find(item => item.id == id);

    if (stop) {
      const route = this.props.routes.find(item => item.id == stop.route_id);
      this.popup
        .setLngLat(stop.geometry.coordinates)
        .setHTML(
          '<div class="stop-popup">' +
            '<p class="title">Route <span>' +
            stop.route_id +
            '</span></p>' +
            '<p class="stops-length">' +
            route.stops.length +
            ' stops</p><p>Stop ' +
            stop.address +
            '</p></div>'
        )
        .addTo(this.theMap);
    }
  };

  hidePopup = () => {
    this.popup.remove();
  };

  render() {
    const { editMode } = this.props;
    return (
      <>
        <div className="flex flex-column relative map-wrapper">
          {editMode && <EditModeMsg />}
          <nav>
            <div>
              <FormControlLabel
                checked={this.state.routeLayerEnabled}
                onClick={this.toggleRoutesLayer}
                control={<Checkbox color="primary" />}
                label="Routes"
                labelPlacement="end"
              />
            </div>
          </nav>
          <div
            className="mapContainer"
            ref={el => {
              this.mapContainer = el;
            }}
          ></div>
        </div>
      </>
    );
  }
}

export default MapWidget;
